import AccessTimeIcon from "@mui/icons-material/AccessTime"
import AddIcon from "@mui/icons-material/Add"
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import SearchIcon from "@mui/icons-material/Search"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { useMemo, useState } from "react"

import Button from "@components/button"
import DialogProvider from "@components/dialog_provider"
import GridTable from "@components/grid-table"
import {
  GRAY_COLOR,
  GRID_HEADER_ITEM,
  PRIMARY,
  WATSAPP_ACTIVE
} from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import {
  archiveAnnouncementReq,
  deleteAnnouncementReq,
  unarchiveAnnouncementReq
} from "@domain/changelog"
import {
  Changelog,
  ChangelogCategory,
  ChangelogChannel,
  ChangelogStatus
} from "@models/changelog"

import { showErrorToast, showSuccessToast } from "@utils/toast"

import { formatShortDate, reactionEmoji, statusMeta } from "../shared"

const STATUS_TABS: { key: "all" | ChangelogStatus; label: string }[] = [
  { key: "all", label: "changelog_filter_all" },
  { key: ChangelogStatus.PUBLISHED, label: "changelog_status_published" },
  { key: ChangelogStatus.SCHEDULED, label: "changelog_status_scheduled" },
  { key: ChangelogStatus.DRAFT, label: "changelog_status_draft" }
]

function firstTwoLangs(a: Changelog): {
  primary?: string
  secondary?: string
} {
  const translations = a.translations || {}
  // Deterministic order (tr → en → rest) so the bold title is always Turkish and
  // the sub-title English — never dependent on JSON key insertion order.
  const langs = Object.keys(translations)
  const ordered = [
    ...["tr", "en"].filter((l) => langs.includes(l)),
    ...langs.filter((l) => l !== "tr" && l !== "en")
  ]
  const primary = translations[ordered[0]]?.title
  const secondary = ordered[1] ? translations[ordered[1]]?.title : undefined
  return { primary, secondary }
}

function CenteredHeader({ label }: { label: string }) {
  return (
    <Box sx={{ width: "100%", textAlign: "center", color: GRID_HEADER_ITEM }}>
      {label}
    </Box>
  )
}

// Forces cell content to span the full cell width and center — DataGrid `align`
// does not reliably center custom renderCell nodes in this setup.
function CenteredCell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {children}
    </Box>
  )
}

function ChannelIcons({ channels }: { channels: ChangelogChannel[] }) {
  return (
    <Stack
      direction="row"
      gap={0.75}
      alignItems="center"
      sx={{ color: GRAY_COLOR }}
    >
      {channels?.includes(ChangelogChannel.EMAIL) && (
        <MailOutlineIcon fontSize="small" />
      )}
      {channels?.includes(ChangelogChannel.WHATSAPP) && (
        <ChatBubbleOutlineIcon
          fontSize="small"
          sx={{ color: WATSAPP_ACTIVE }}
        />
      )}
      {channels?.includes(ChangelogChannel.PUSH) && (
        <NotificationsNoneIcon fontSize="small" sx={{ color: PRIMARY }} />
      )}
    </Stack>
  )
}

function ActionsCell({ row }: { row: Changelog }) {
  const { translate, refetchAnnouncements, openEditAnnouncement } =
    useChangelogPageContext()
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isPublished = row.status === ChangelogStatus.PUBLISHED
  const isArchived = row.status === ChangelogStatus.ARCHIVED

  const handleArchive = async () => {
    setBusy(true)
    const response = await archiveAnnouncementReq(row._id)
    setBusy(false)
    if (response.isSuccess) {
      showSuccessToast(translate("changelog_archived"))
      refetchAnnouncements()
    } else {
      showErrorToast(
        response.errorMessage || translate("changelog_save_failed")
      )
    }
  }

  const handleReactivate = async () => {
    setBusy(true)
    const response = await unarchiveAnnouncementReq(row._id)
    setBusy(false)
    if (response.isSuccess) {
      showSuccessToast(translate("changelog_reactivated"))
      refetchAnnouncements()
    } else {
      showErrorToast(
        response.errorMessage || translate("changelog_save_failed")
      )
    }
  }

  const handleDelete = async () => {
    setBusy(true)
    const response = await deleteAnnouncementReq(row._id)
    setBusy(false)
    setConfirmDelete(false)
    if (response.isSuccess) {
      showSuccessToast(translate("changelog_deleted"))
      refetchAnnouncements()
    } else {
      showErrorToast(
        response.errorMessage || translate("changelog_save_failed")
      )
    }
  }

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <Tooltip title={translate("edit")}>
        <IconButton
          size="small"
          onClick={() => openEditAnnouncement(row)}
          sx={{ color: GRAY_COLOR }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isPublished && (
        <Tooltip title={translate("changelog_archive")}>
          <IconButton
            size="small"
            onClick={handleArchive}
            disabled={busy}
            sx={{ color: GRAY_COLOR }}
          >
            <ArchiveOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isArchived && (
        <Tooltip title={translate("changelog_reactivate")}>
          <IconButton
            size="small"
            onClick={handleReactivate}
            disabled={busy}
            sx={{ color: PRIMARY }}
          >
            <RestoreOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isPublished && (
        <Tooltip title={translate("delete")}>
          <IconButton
            size="small"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            sx={{ color: GRAY_COLOR }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {confirmDelete && (
        <DialogProvider
          title={translate("changelog_delete_announcement")}
          isOpen={confirmDelete}
          setClose={() => setConfirmDelete(false)}
          size="small"
          leftButton={{
            text: translate("cancel"),
            color: "secondary",
            onClick: () => setConfirmDelete(false)
          }}
          rightButton={{
            text: translate("delete"),
            color: "error",
            onClick: handleDelete,
            loading: busy
          }}
        >
          <Typography sx={{ fontSize: "0.9rem" }}>
            {translate("changelog_delete_announcement_desc")}
          </Typography>
        </DialogProvider>
      )}
    </Box>
  )
}

export default function AnnouncementsSection() {
  const {
    translate,
    announcements,
    announcementsLoading,
    categories,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    openCreateAnnouncement,
    togglePreview,
    openDetail
  } = useChangelogPageContext()

  const categoryMap = useMemo(() => {
    const map = new Map<string, ChangelogCategory>()
    categories.forEach((c) => map.set(c._id, c))
    return map
  }, [categories])

  const counts = useMemo(() => {
    const by: Record<string, number> = { all: announcements.length }
    announcements.forEach((a) => {
      by[a.status] = (by[a.status] || 0) + 1
    })
    return by
  }, [announcements])

  const filtered = useMemo(() => {
    return announcements.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) {
        return false
      }
      if (search) {
        const hay = Object.values(a.translations || {})
          .map((t) => `${t.title} ${t.content}`)
          .join(" ")
          .toLowerCase()
        if (!hay.includes(search.toLowerCase())) {
          return false
        }
      }
      return true
    })
  }, [announcements, statusFilter, search])

  const columns: GridColDef[] = [
    {
      field: "title",
      flex: 3,
      minWidth: 220,
      headerName: translate("changelog_col_title"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_title")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const { primary, secondary } = firstTwoLangs(params.row as Changelog)
        return (
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }} noWrap>
              {primary || translate("changelog_untitled")}
            </Typography>
            {secondary && (
              <Typography sx={{ color: GRAY_COLOR, fontSize: "0.8rem" }} noWrap>
                {secondary}
              </Typography>
            )}
          </Box>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "category",
      flex: 1.4,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_category"),
      renderHeader: () => (
        <CenteredHeader label={translate("changelog_col_category")} />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as Changelog
        const category = categoryMap.get(row.categoryId)
        const categoryName = category
          ? category.translations?.tr?.name ||
            Object.values(category.translations || {})[0]?.name
          : undefined
        if (!categoryName) {
          return (
            <CenteredCell>
              <Typography sx={{ color: GRAY_COLOR }}>—</Typography>
            </CenteredCell>
          )
        }
        return (
          <CenteredCell>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: category?.color || GRAY_COLOR
                }}
              />
              <Typography sx={{ fontSize: "0.85rem" }}>
                {categoryName}
              </Typography>
            </Stack>
          </CenteredCell>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "status",
      flex: 1,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_status"),
      renderHeader: () => (
        <CenteredHeader label={translate("changelog_col_status")} />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const status = statusMeta((params.row as Changelog).status)
        return (
          <CenteredCell>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                height: 24,
                px: 1.25,
                borderRadius: 1.5,
                fontSize: "0.78rem",
                fontWeight: 600,
                lineHeight: 1,
                color: status.color,
                background: status.bg
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: status.color
                }}
              />
              {translate(status.key)}
            </Box>
          </CenteredCell>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "channels",
      flex: 1,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_channels"),
      renderHeader: () => (
        <CenteredHeader label={translate("changelog_col_channels")} />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <CenteredCell>
          <ChannelIcons channels={(params.row as Changelog).channels} />
        </CenteredCell>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "reaction",
      flex: 1,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_reaction"),
      renderHeader: () => (
        <CenteredHeader label={translate("changelog_col_reaction")} />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const a = params.row as Changelog
        if (!a.reactionCount || a.reactionCount <= 0) {
          return (
            <CenteredCell>
              <Typography sx={{ color: GRAY_COLOR }}>—</Typography>
            </CenteredCell>
          )
        }
        return (
          <CenteredCell>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                {(a.reactionAvg || 0).toFixed(1)}
              </Typography>
              <span>{reactionEmoji(a.reactionAvg || 0)}</span>
              <Typography sx={{ color: GRAY_COLOR, fontSize: "0.8rem" }}>
                ({a.reactionCount})
              </Typography>
            </Stack>
          </CenteredCell>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "date",
      flex: 1.2,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_date"),
      renderHeader: () => (
        <CenteredHeader label={translate("changelog_col_date")} />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const a = params.row as Changelog
        const isScheduled = a.status === ChangelogStatus.SCHEDULED
        const status = statusMeta(a.status)
        const dateValue = isScheduled
          ? a.scheduledAt
          : a.publishedAt || a.createdAt
        return (
          <CenteredCell>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {isScheduled && (
                <AccessTimeIcon fontSize="small" sx={{ color: status.color }} />
              )}
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: isScheduled ? status.color : "text.primary"
                }}
              >
                {formatShortDate(dateValue)}
              </Typography>
            </Stack>
          </CenteredCell>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "actions",
      minWidth: 100,
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      headerName: "",
      renderHeader: () => <Box />,
      renderCell: (params: GridRenderCellParams) => (
        <ActionsCell row={params.row as Changelog} />
      ),
      sortable: false,
      disableColumnMenu: true
    }
  ]

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
            {translate("changelog_announcements")}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
            {announcements.length} {translate("changelog_announcement_lower")}
          </Typography>
        </Box>
        <Stack direction="row" gap={1.5}>
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={togglePreview}
            sx={{ minWidth: "8rem", background: "white" }}
          >
            {translate("changelog_preview")}
          </Button>
          <Button startIcon={<AddIcon />} onClick={openCreateAnnouncement}>
            {translate("changelog_new_announcement")}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translate("changelog_search_placeholder")}
          sx={{
            flex: 1,
            maxWidth: "26rem",
            background: "white",
            borderRadius: 1
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: GRAY_COLOR }} />
              </InputAdornment>
            )
          }}
        />
        <Stack direction="row" gap={0.5}>
          {STATUS_TABS.map((tab) => {
            const isActive = statusFilter === tab.key
            return (
              <Box
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                sx={{
                  cursor: "pointer",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  fontSize: "0.85rem",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "white" : "text.secondary",
                  background: isActive ? PRIMARY : "transparent"
                }}
              >
                {translate(tab.label)} {counts[tab.key] || 0}
              </Box>
            )
          })}
        </Stack>
      </Stack>

      <GridTable
        paginationHidden
        loading={announcementsLoading}
        rows={filtered}
        columns={columns}
        rowCount={filtered.length}
        paginationMode="client"
        getRowId={(row: Changelog) => row._id}
        disableColumnSelector={true}
        noRowsTableText="changelog_empty_announcements"
        onRowClick={(row: Changelog) => openDetail(row)}
      />
    </Box>
  )
}
